import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { X, Send, Smile, MoreHorizontal, LinkIcon, Code, Trash2, Reply } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';

import { useAuthor } from '@/hooks/useAuthor';
import { useComments } from '@/hooks/useComments';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { usePostComment } from '@/hooks/usePostComment';
import { useToast } from '@/hooks/useToast';
import { useDittoThemeContext } from './DittoThemeProvider';
import { EmojiPicker } from './EmojiPicker';
import { EmojiSuggestions } from './EmojiSuggestions';
import { LoginArea } from './auth/LoginArea';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useEmojiShortcode } from '@/hooks/useEmojiShortcode';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { genUserName } from '@/lib/genUserName';
import { WashiTape } from './WashiTape';
import {
  getTitle,
  getAllImageUrls,
  getImageAlt,
  formatPolaroidDate,
  formatRelativeTime,
  getHashtags,
  getContentWarning,
  seedFromHex,
  getTapeVariant,
} from '@/lib/polaroid-utils';
import { ContentWarningOverlay } from './ContentWarningOverlay';
import { cn } from '@/lib/utils';

/** Ruled notebook lines background */
const RULED_LINES = 'repeating-linear-gradient(to bottom, transparent, transparent 17px, rgba(180,200,220,0.25) 17px, rgba(180,200,220,0.25) 18px)';

/** Tape solid colors — CSS variables that adapt to theme */
const TAPE_SOLID_VARS = [
  'var(--tape-0-solid)', 'var(--tape-1-solid)', 'var(--tape-2-solid)', 'var(--tape-3-solid)',
  'var(--tape-4-solid)', 'var(--tape-5-solid)', 'var(--tape-6-solid)', 'var(--tape-7-solid)',
];

interface PhotoDetailProps {
  event: NostrEvent;
  onClose: () => void;
}

export function PhotoDetail({ event, onClose }: PhotoDetailProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeTheme } = useDittoThemeContext();
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { toast } = useToast();
  const author = useAuthor(event.pubkey);
  const displayName = author.data?.metadata?.name ?? genUserName(event.pubkey);
  const avatar = author.data?.metadata?.picture;
  const title = getTitle(event);
  const images = getAllImageUrls(event);
  const altText = getImageAlt(event);
  const date = formatPolaroidDate(event.created_at);

  const contentWarning = getContentWarning(event);
  const [cwRevealed, setCwRevealed] = useState(false);
  const [cwPeeling, setCwPeeling] = useState(false);
  const hasCw = !!contentWarning && !cwRevealed;
  const showBlur = hasCw && !cwPeeling;
  const isOwnPost = user?.pubkey === event.pubkey;
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const dragRef = useRef<{ startY: number; currentY: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(onClose, 250);
  }, [onClose]);

  // Lock body scroll while overlay is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleClose]);

  const handleTouchStart = (e: React.TouchEvent) => {
    dragRef.current = { startY: e.touches[0].clientY, currentY: e.touches[0].clientY };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!dragRef.current) return;
    dragRef.current.currentY = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (!dragRef.current) return;
    const diff = dragRef.current.currentY - dragRef.current.startY;
    if (diff > 100) handleClose();
    dragRef.current = null;
  };

  const handleImageSwipe = (direction: 'left' | 'right') => {
    if (direction === 'left' && currentImage < images.length - 1) {
      setCurrentImage(prev => prev + 1);
    } else if (direction === 'right' && currentImage > 0) {
      setCurrentImage(prev => prev - 1);
    }
  };

  const handleDelete = useCallback(async () => {
    if (!confirm('Delete this polaroid? This cannot be undone.')) return;
    setIsDeleting(true);
    try {
      await publishEvent({
        kind: 5,
        content: '',
        tags: [
          ['e', event.id],
          ['k', String(event.kind)],
        ],
      });
      // Invalidate all picture queries so feed and album refresh
      queryClient.invalidateQueries({ queryKey: ['pictures'] });
      handleClose();
    } catch (error) {
      console.error('Delete failed:', error);
      setIsDeleting(false);
    }
  }, [event.id, event.kind, publishEvent, queryClient, handleClose]);

  const handleCopyLink = useCallback(() => {
    const nevent = nip19.neventEncode({ id: event.id, author: event.pubkey });
    const url = `${window.location.origin}/${nevent}`;
    navigator.clipboard.writeText(url).then(() => {
      toast({ title: 'Link copied', description: 'Post link copied to clipboard' });
    }).catch(() => {
      // Fallback for older browsers
      toast({ title: 'Link', description: url });
    });
  }, [event.id, event.pubkey, toast]);

  const imgTouchRef = useRef<{ startX: number } | null>(null);

  return (
    <>
    <div
      ref={overlayRef}
      className={cn(
        'fixed inset-0 z-[100] overflow-y-auto',
        'backdrop-blur-sm',
        'transition-opacity duration-300',
        isClosing ? 'opacity-0' : 'opacity-100',
      )}
      style={{
        backgroundColor: activeTheme
          ? `${activeTheme.colors.background}d9`  // theme bg at 85% opacity
          : 'rgba(var(--pol-bg), 0.85)',
      }}
      onClick={(e) => { if (e.target === overlayRef.current) handleClose(); }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className="fixed top-4 right-4 z-[110] w-11 h-11 flex items-center justify-center bg-polaroid-white text-pol-text shadow-[0_2px_8px_rgba(45,27,78,0.15)] hover:shadow-[0_4px_12px_rgba(45,27,78,0.2)] hover:-translate-y-0.5 transition-all active:scale-95"
        aria-label="Close"
      >
        <X size={22} strokeWidth={2.5} />
      </button>

      {/* Scrollable content area */}
      <div className="min-h-full flex flex-col items-center px-4 py-8">
        {/* Polaroid card — z-30 when menu open so dropdown paints above the z-20 comments */}
        <div
          className={cn(
            'relative w-full max-w-sm',
            'transition-all duration-300',
            isClosing ? 'scale-90 opacity-0' : 'scale-100 opacity-100',
            menuOpen && 'z-30',
          )}
        >
          <div className="relative bg-polaroid-white rounded-[3px] p-3 pb-0 shadow-[0_8px_40px_rgba(0,0,0,0.3)]" style={{ transform: 'rotate(-1deg)' }}>
            {/* Washi tape */}
            <WashiTape eventId={event.id} />

            {/* Photo */}
            <div
              className="relative overflow-hidden bg-cream-dark"
              onTouchStart={(e) => { imgTouchRef.current = { startX: e.touches[0].clientX }; }}
              onTouchEnd={(e) => {
                if (!imgTouchRef.current) return;
                const diff = e.changedTouches[0].clientX - imgTouchRef.current.startX;
                if (Math.abs(diff) > 50) handleImageSwipe(diff < 0 ? 'left' : 'right');
                imgTouchRef.current = null;
              }}
            >
              <img
                src={images[currentImage]}
                alt={altText}
                className={cn(
                  'w-full max-h-[50vh] object-contain bg-cream-dark transition-all duration-300',
                  showBlur && 'blur-lg',
                )}
              />
              {hasCw && (
                <ContentWarningOverlay
                  reason={contentWarning}
                  eventId={event.id}
                  variant="detail"
                  onReveal={() => {
                    setCwPeeling(true);
                    setCwRevealed(true);
                  }}
                />
              )}
            </div>

            {/* Image dots for multi-image */}
            {images.length > 1 && (
              <div className="flex gap-2 justify-center pt-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImage(i)}
                    className={cn(
                      'w-2 h-2 rounded-full transition-colors',
                      i === currentImage ? 'bg-pol-pink' : 'bg-pol-pink/30',
                    )}
                  />
                ))}
              </div>
            )}

            {/* Caption area — inside the polaroid white strip */}
            <div className="px-1 pt-2 pb-3 space-y-1.5">
              {(title || event.content) && (
                <p className="font-hand text-[18px] text-pol-text leading-snug">
                  {title || (event.content.length > 80 ? event.content.slice(0, 80) + '...' : event.content)}
                </p>
              )}

              {title && event.content && (
                <p className="font-sans text-[13px] text-pol-text-secondary leading-relaxed">
                  {event.content.length > 120 ? event.content.slice(0, 120) + '...' : event.content}
                </p>
              )}

              {(() => {
                const tags = getHashtags(event);
                if (tags.length === 0) return null;
                return (
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => {
                          handleClose();
                          setTimeout(() => navigate(`/tag/${encodeURIComponent(tag)}`), 260);
                        }}
                        className="font-hand text-[13px] bg-cream text-pol-text px-2 py-0.5 cursor-pointer hover:brightness-90 active:scale-95 transition-all"
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                );
              })()}

              <p className="font-hand font-bold text-sm text-pol-text-caption">{date}</p>

              {/* Author row + menu */}
              <div className="flex items-center justify-between pt-1">
                <button
                  onClick={() => {
                    handleClose();
                    setTimeout(() => navigate(`/${nip19.npubEncode(event.pubkey)}`), 260);
                  }}
                  className="flex items-center gap-2 group"
                >
                  <div className="w-6 h-6 rounded-full overflow-hidden border border-pol-pink/30 flex-shrink-0">
                    {avatar ? (
                      <img src={avatar} alt={displayName} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-pol-pink to-pol-lilac" />
                    )}
                  </div>
                  <span className="text-[13px] text-pol-text-secondary group-hover:text-pol-text transition-colors">
                    {displayName}
                  </span>
                </button>

                {/* Post menu — inline (no portal) so it lives in the card's stacking context;
                     the card wrapper gets z-30 when open to paint above the z-20 comments */}
                <DropdownMenuPrimitive.Root open={menuOpen} onOpenChange={setMenuOpen}>
                  <DropdownMenuPrimitive.Trigger asChild>
                    <button
                      className="w-8 h-8 flex items-center justify-center text-pol-text-secondary hover:text-pol-text transition-colors active:scale-90"
                      aria-label="Post options"
                    >
                      <MoreHorizontal size={18} />
                    </button>
                  </DropdownMenuPrimitive.Trigger>
                  <DropdownMenuPrimitive.Content
                    align="end"
                    sideOffset={4}
                    className="z-[200] min-w-[180px] overflow-hidden border bg-polaroid-white p-1 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
                  >
                    <DropdownMenuPrimitive.Item onClick={handleCopyLink} className="flex items-center font-hand text-base gap-2 cursor-pointer px-2 py-1.5 outline-none hover:bg-cream transition-colors">
                      <LinkIcon size={15} />
                      Copy link
                    </DropdownMenuPrimitive.Item>
                    <DropdownMenuPrimitive.Item onClick={() => setJsonOpen(true)} className="flex items-center font-hand text-base gap-2 cursor-pointer px-2 py-1.5 outline-none hover:bg-cream transition-colors">
                      <Code size={15} />
                      View event JSON
                    </DropdownMenuPrimitive.Item>
                    {isOwnPost && (
                      <>
                        <DropdownMenuPrimitive.Separator className="h-px my-1 bg-cream-dark/50" />
                        <DropdownMenuPrimitive.Item
                          onClick={handleDelete}
                          disabled={isDeleting}
                          className="flex items-center font-hand text-base gap-2 cursor-pointer px-2 py-1.5 outline-none text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 size={15} />
                          {isDeleting ? 'Deleting...' : 'Delete post'}
                        </DropdownMenuPrimitive.Item>
                      </>
                    )}
                  </DropdownMenuPrimitive.Content>
                </DropdownMenuPrimitive.Root>
              </div>
            </div>
          </div>
        </div>

        {/* Comments section — z-20 so emoji picker/suggestions paint above the card */}
        <div className="relative z-20 w-full max-w-sm mt-6" style={{ transform: 'rotate(0deg)' }}>
          <PhotoComments event={event} onNavigate={(path) => { handleClose(); setTimeout(() => navigate(path), 260); }} />
        </div>
      </div>
    </div>

    {/* Event JSON dialog — rendered outside the overlay so z-index works */}
    <Dialog open={jsonOpen} onOpenChange={setJsonOpen}>
      <DialogContent className="max-w-md p-0 gap-0 bg-polaroid-white z-[200]">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="font-hand text-2xl text-pol-text">Event JSON</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh]">
          <pre className="px-5 pb-5 text-[11px] font-mono text-pol-text-secondary whitespace-pre-wrap break-all leading-relaxed select-all">
            {JSON.stringify(event, null, 2)}
          </pre>
        </ScrollArea>
      </DialogContent>
    </Dialog>
    </>
  );
}

// ── Comment note ─────────────────────────────────────────────────────

/** Max characters before "read more" truncation */
const COMMENT_TRUNCATE_LENGTH = 180;

interface CommentNoteProps {
  comment: NostrEvent;
  onNavigate: (path: string) => void;
  /** Name of the person this comment is replying to (for nested replies) */
  replyToName?: string;
  /** Whether to show the reply button */
  showReplyButton?: boolean;
  /** Callback when reply button is clicked */
  onReply?: () => void;
  /** Whether this is an indented reply (smaller tape, no rotation) */
  isReply?: boolean;
}

/** A single comment rendered as notebook paper */
function CommentNote({ comment, onNavigate, replyToName, showReplyButton, onReply, isReply }: CommentNoteProps) {
  const author = useAuthor(comment.pubkey);
  const metadata = author.data?.metadata;
  const name = metadata?.name ?? genUserName(comment.pubkey);
  const timeAgo = formatRelativeTime(comment.created_at);
  const [expanded, setExpanded] = useState(false);

  const isLong = comment.content.length > COMMENT_TRUNCATE_LENGTH;
  const displayContent = (!expanded && isLong)
    ? comment.content.slice(0, COMMENT_TRUNCATE_LENGTH)
    : comment.content;

  const rotation = isReply ? 0 : (seedFromHex(comment.id, 20, 24) % 5) - 2;
  const tapeIdx = getTapeVariant(comment.id);
  const tapeColor = TAPE_SOLID_VARS[tapeIdx % TAPE_SOLID_VARS.length];
  const tapeRotation = isReply ? 0 : (seedFromHex(comment.id, 6, 8) % 10) - 5;

  return (
    <div style={{ transform: `rotate(${rotation}deg)` }}>
      <div className="relative">
        {/* Horizontal tape strip — smaller for replies */}
        <div
          className="absolute left-1/2 z-10"
          style={{
            width: isReply ? '40px' : '60px',
            height: isReply ? '12px' : '16px',
            top: isReply ? '-6px' : '-8px',
            backgroundColor: tapeColor,
            opacity: 0.7,
            transform: `translateX(-50%) rotate(${tapeRotation}deg)`,
          }}
        />

        {/* Notebook paper body */}
        <div
          className="relative px-3 pt-4 pb-3"
          style={{
            backgroundColor: '#FFFFFF',
            backgroundImage: RULED_LINES,
            backgroundPosition: '0 8px',
            boxShadow: '1px 2px 4px rgba(0,0,0,0.08)',
          }}
        >
          {/* "reply to..." header */}
          {replyToName && (
            <p className="font-hand text-[11px] text-pol-blue mb-1 -mt-0.5">
              reply to {replyToName}
            </p>
          )}

          {/* Author row */}
          <Link
            to={`/${nip19.npubEncode(comment.pubkey)}`}
            onClick={(e) => { e.preventDefault(); onNavigate(`/${nip19.npubEncode(comment.pubkey)}`); }}
            className="flex items-center gap-1.5 mb-1.5 group"
          >
            <div className="w-5 h-5 rounded-full overflow-hidden border border-pol-pink/30 flex-shrink-0">
              {metadata?.picture ? (
                <img src={metadata.picture} alt={name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-pol-pink/30 to-pol-lilac/30" />
              )}
            </div>
            <span className="font-hand text-[13px] text-pol-text font-bold group-hover:text-pol-pink transition-colors truncate">
              {name}
            </span>
            <span className="font-hand text-[11px] text-pol-text-secondary flex-shrink-0">
              {timeAgo}
            </span>
          </Link>

          {/* Comment text with truncation */}
          <p
            className="font-hand text-[15px] leading-[17px] break-words overflow-hidden"
            style={{ color: '#2D1B4E', overflowWrap: 'anywhere' }}
          >
            {displayContent}
            {!expanded && isLong && '... '}
            {!expanded && isLong && (
              <button
                onClick={() => setExpanded(true)}
                className="inline font-hand text-[13px] text-pol-blue hover:text-pol-blue/80 transition-colors"
              >
                read more
              </button>
            )}
          </p>

          {/* Reply button — bottom-right of paper */}
          {showReplyButton && (
            <div className="flex justify-end pt-1.5 -mb-0.5">
              <button
                onClick={onReply}
                className="flex items-center gap-1 text-pol-text-secondary/40 hover:text-pol-blue transition-colors active:scale-90"
                aria-label="Reply"
              >
                <Reply size={13} className="scale-x-[-1]" />
                <span className="font-hand text-[11px]">reply</span>
              </button>
            </div>
          )}
        </div>

        {/* Peeling corner — only on non-replies (replies are simpler) */}
        {!isReply && (
          <div className="absolute bottom-0 right-0 overflow-hidden" style={{ width: '14px', height: '14px' }}>
            <div className="absolute inset-0" style={{ backgroundColor: '#FDF6EC' }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, transparent 50%, #E8E8E8 50%)' }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, transparent 45%, rgba(0,0,0,0.08) 50%, transparent 55%)' }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inline reply compose ──────────────────────────────────────────────

interface InlineReplyFormProps {
  rootEvent: NostrEvent;
  parentComment: NostrEvent;
  onClose: () => void;
}

/** Compact notebook-paper reply form that appears beneath a comment */
function InlineReplyForm({ rootEvent, parentComment, onClose }: InlineReplyFormProps) {
  const { mutate: postComment, isPending } = usePostComment();
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Focus input when form opens
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    postComment(
      { content: text.trim(), root: rootEvent, reply: parentComment },
      { onSuccess: () => { setText(''); onClose(); } },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="ml-8 mt-2">
      <div
        className="relative px-3 pt-2.5 pb-2 transition-shadow duration-200 has-[:focus]:shadow-[1px_3px_8px_rgba(0,0,0,0.12)]"
        style={{
          backgroundColor: '#FFFFFF',
          backgroundImage: RULED_LINES,
          backgroundPosition: '0 6px',
          boxShadow: '1px 2px 4px rgba(0,0,0,0.06)',
        }}
      >
        {/* Blue left accent */}
        <div className="absolute left-0 top-2 bottom-2 w-[3px] bg-pol-blue/60" />

        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write a reply..."
          className="w-full bg-transparent font-hand text-[14px] leading-[17px] text-[#2D1B4E] placeholder:text-[#2D1B4E]/30 resize-none outline-none min-h-[34px] caret-pol-blue"
          style={{ overflowWrap: 'anywhere' }}
          disabled={isPending}
          rows={1}
        />
        <div className="flex items-center justify-between pt-0.5">
          <button
            type="button"
            onClick={onClose}
            className="font-hand text-[12px] text-pol-text-secondary/50 hover:text-pol-text-secondary transition-colors"
          >
            cancel
          </button>
          <button
            type="submit"
            disabled={!text.trim() || isPending}
            className="font-hand text-[13px] text-pol-text-secondary hover:text-pol-text disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
          >
            <Send size={11} />
            {isPending ? 'Posting...' : 'Reply'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ── Comment thread (top-level + one level of replies) ─────────────────

/** Helper: get display name for a pubkey from a list of comments */
function useCommentAuthorName(pubkey: string): string {
  const author = useAuthor(pubkey);
  return author.data?.metadata?.name ?? genUserName(pubkey);
}

/** Renders a top-level comment with its direct replies indented beneath */
function CommentThread({
  comment,
  replies,
  rootEvent,
  onNavigate,
  replyingTo,
  setReplyingTo,
  user,
}: {
  comment: NostrEvent;
  replies: NostrEvent[];
  rootEvent: NostrEvent;
  onNavigate: (path: string) => void;
  replyingTo: string | null;
  setReplyingTo: (id: string | null) => void;
  user: { pubkey: string } | undefined;
}) {
  const parentName = useCommentAuthorName(comment.pubkey);

  return (
    <div className="space-y-3">
      {/* Top-level comment */}
      <CommentNote
        comment={comment}
        onNavigate={onNavigate}
        showReplyButton={!!user}
        onReply={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
      />

      {/* Inline reply form — directly beneath this comment */}
      {replyingTo === comment.id && user && (
        <InlineReplyForm
          rootEvent={rootEvent}
          parentComment={comment}
          onClose={() => setReplyingTo(null)}
        />
      )}

      {/* One level of replies — indented */}
      {replies.length > 0 && (
        <div className="ml-8 space-y-3 border-l-2 border-pol-blue/30 pl-3">
          {replies.map((reply) => (
            <div key={reply.id}>
              <CommentNote
                comment={reply}
                onNavigate={onNavigate}
                replyToName={parentName}
                isReply
                showReplyButton={!!user}
                onReply={() => setReplyingTo(replyingTo === reply.id ? null : reply.id)}
              />
              {/* Allow replying to replies too — the reply goes to the same parent level */}
              {replyingTo === reply.id && user && (
                <InlineReplyForm
                  rootEvent={rootEvent}
                  parentComment={reply}
                  onClose={() => setReplyingTo(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Comments area ────────────────────────────────────────────────────

function PhotoComments({ event, onNavigate }: { event: NostrEvent; onNavigate: (path: string) => void }) {
  const { data: commentsData, isLoading } = useComments(event);
  const { user } = useCurrentUser();
  const { mutate: postComment, isPending } = usePostComment();
  const [commentText, setCommentText] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const { suggestions, showSuggestions, query, handleChange: handleShortcode, selectSuggestion, dismiss: dismissShortcode } = useEmojiShortcode();

  const comments = commentsData?.topLevelComments ?? [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || !user) return;
    postComment(
      { content: commentText.trim(), root: event },
      {
        onSuccess: () => {
          setCommentText('');
          dismissShortcode();
        },
      },
    );
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value);
    handleShortcode(e.target.value, e.target.selectionStart ?? e.target.value.length);
  };

  const insertEmoji = (emoji: string) => {
    const el = commentRef.current;
    if (el) {
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const newValue = el.value.slice(0, start) + emoji + el.value.slice(end);
      setCommentText(newValue);
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = start + emoji.length;
        el.focus();
      });
    } else {
      setCommentText(prev => prev + emoji);
    }
  };

  return (
    <div className="space-y-4">
      {/* Comment input — notebook paper style */}
      {user ? (
        <form onSubmit={handleSubmit} className="relative">
          {/* Emoji shortcode suggestions — above the form, outside the rotated paper */}
          {showSuggestions && (
            <div className="absolute left-0 right-0 z-50" style={{ bottom: '100%', marginBottom: '4px' }}>
              <EmojiSuggestions
                suggestions={suggestions}
                query={query}
                onSelect={(emoji) => {
                  const el = commentRef.current;
                  const cursorPos = el?.selectionStart ?? commentText.length;
                  const { newValue, newCursor } = selectSuggestion(emoji, commentText, cursorPos);
                  setCommentText(newValue);
                  requestAnimationFrame(() => {
                    if (el) {
                      el.selectionStart = el.selectionEnd = newCursor;
                      el.focus();
                    }
                  });
                }}
              />
            </div>
          )}
          <div className="relative group/comment" style={{ transform: 'rotate(0.5deg)' }}>
            <div
              className="relative px-3 pt-3 pb-2 transition-shadow duration-200 has-[:focus]:shadow-[1px_3px_8px_rgba(0,0,0,0.12)]"
              style={{
                backgroundColor: '#FFFFFF',
                backgroundImage: RULED_LINES,
                backgroundPosition: '0 6px',
                boxShadow: '1px 2px 4px rgba(0,0,0,0.08)',
              }}
            >
              {/* Pen-stroke accent on the left edge — appears on focus */}
              <div className="absolute left-0 top-2 bottom-2 w-[3px] bg-pol-pink/0 transition-colors duration-200 has-[~textarea:focus]:bg-pol-pink/60 group-focus-within/comment:bg-pol-pink/60" />

              <textarea
                ref={commentRef}
                value={commentText}
                onChange={handleCommentChange}
                onBlur={() => {
                  // Delay dismiss so suggestion clicks can register
                  setTimeout(dismissShortcode, 200);
                }}
                placeholder="Write a note..."
                className="w-full bg-transparent font-hand text-[15px] leading-[17px] text-[#2D1B4E] placeholder:text-[#2D1B4E]/30 resize-none outline-none min-h-[51px] caret-pol-pink"
                style={{ overflowWrap: 'anywhere' }}
                disabled={isPending}
                rows={2}
              />
              <div className="flex items-center justify-between pt-1">
                <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex-shrink-0 w-7 h-7 flex items-center justify-center hover:bg-cream-dark/60 transition-colors text-pol-text-secondary/50 hover:text-pol-text-secondary"
                    >
                      <Smile size={16} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-0 shadow-xl" side="top" align="start">
                    <EmojiPicker onSelect={(emoji) => {
                      insertEmoji(emoji);
                      setEmojiOpen(false);
                    }} />
                  </PopoverContent>
                </Popover>
                <button
                  type="submit"
                  disabled={!commentText.trim() || isPending}
                  className="font-hand text-sm text-pol-text-secondary hover:text-pol-text disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                >
                  <Send size={13} />
                  {isPending ? 'Posting...' : 'Post'}
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div
          className="relative px-4 py-4 text-center"
          style={{
            backgroundColor: '#FFFFFF',
            boxShadow: '1px 2px 4px rgba(0,0,0,0.08)',
          }}
        >
          <p className="font-hand text-[15px] text-pol-text-secondary mb-3">Sign in to leave a note</p>
          <LoginArea className="max-w-48 mx-auto" />
        </div>
      )}

      {/* Comments list */}
      {isLoading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-16"
              style={{
                backgroundColor: '#FFFFFF',
                boxShadow: '1px 2px 4px rgba(0,0,0,0.06)',
                transform: `rotate(${(i % 3) - 1}deg)`,
              }}
            />
          ))}
        </div>
      ) : comments.length > 0 ? (
        <div className="space-y-5">
          {comments.map((comment) => (
            <CommentThread
              key={comment.id}
              comment={comment}
              replies={commentsData?.getDirectReplies(comment.id) ?? []}
              rootEvent={event}
              onNavigate={onNavigate}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              user={user}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
